export type PreferredWallet = "apple_pay" | "google_pay";

interface WalletEnvironmentInfo {
  isAppleDevice: boolean;
  isSafari: boolean;
  isAndroid: boolean;
  isChromeLike: boolean;
  isIphone: boolean;
  isMobileViewport: boolean;
}

interface WalletButtonVisibility {
  showApplePay: boolean;
  showGooglePay: boolean;
}

const getWalletEnvironmentInfo = (): WalletEnvironmentInfo => {
  if (typeof window === "undefined") {
    return {
      isAppleDevice: false,
      isSafari: false,
      isAndroid: false,
      isChromeLike: false,
      isIphone: false,
      isMobileViewport: false,
    };
  }

  const ua = window.navigator.userAgent.toLowerCase();
  const isAppleDevice =
    /iphone|ipad|ipod|macintosh|mac os x/.test(ua) &&
    !/android/.test(ua);
  const isSafari =
    /safari/.test(ua) &&
    !/chrome|crios|fxios|edg|opr|opera|android/.test(ua);
  const isAndroid = /android/.test(ua);
  const isChromeLike = /chrome|crios|edg/.test(ua) && !/opr|opera/.test(ua);
  const isIphone = /iphone/.test(ua);
  const isMobileViewport = window.innerWidth <= 991;

  return {
    isAppleDevice,
    isSafari,
    isAndroid,
    isChromeLike,
    isIphone,
    isMobileViewport,
  };
};

export const getUnsupportedWalletMessage = (
  preferredWallet?: PreferredWallet,
): string | null => {
  if (!preferredWallet) return null;

  const env = getWalletEnvironmentInfo();

  if (preferredWallet === "apple_pay") {
    if (!env.isAppleDevice || !env.isSafari) {
      return "Apple Pay works on Apple devices in Safari. Please open this on iPhone/iPad/Mac Safari or use card/Google Pay on a supported device.";
    }
    return null;
  }

  if (preferredWallet === "google_pay") {
    if (!env.isAndroid && !env.isChromeLike) {
      return "Google Pay works best on Android or desktop Chrome/Edge (Windows/Mac). Please switch device/browser or use card payment.";
    }
    return null;
  }

  return null;
};

export const getWalletButtonVisibility = (): WalletButtonVisibility => {
  if (typeof window === "undefined") {
    return { showApplePay: false, showGooglePay: false };
  }

  const env = getWalletEnvironmentInfo();

  // Show wallet buttons only on mobile viewport.
  if (!env.isMobileViewport) {
    return { showApplePay: false, showGooglePay: false };
  }

  if (env.isAndroid) {
    return { showApplePay: false, showGooglePay: true };
  }

  if (env.isIphone) {
    return { showApplePay: true, showGooglePay: false };
  }

  return { showApplePay: false, showGooglePay: false };
};
